import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import tddaLogo from "../../../Assets/images/logo2.png";
import { Backdrop, Badge, Button, IconButton, LinearProgress, Menu, MenuItem, ListItemIcon } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { FC, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import UserMenu from "../UserMenu/UserMenu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ROUTE_URLS } from "src/Shared/Enums/RouteUrls.enum";
import { Tabs } from "./Header.tabs";
import { getActiveTabLink } from "./Header.utils";
import { useStyles } from "./Header.styles";
import SettingsIcon from "@mui/icons-material/Settings";
import ListIcon from "@mui/icons-material/List";
import PeopleIcon from "@mui/icons-material/People";
import Permission from "src/Shared/Components/Permission/Permission";

const Header: FC = () => {
  const {
    rootStore: { authStore, uiStore, companyStore },
  } = useStore();

  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isGranted } = authStore;
  const { companyOverview, openClientContext, prospectsCount, getProspectsCount } =
    companyStore;

  useEffect(() => {
    if (user) getProspectsCount();
  }, [user]);

  // The open client comes from the shared context every client-scoped screen publishes, so the
  // wizard, SOP and setup-copy routes light the right tab however they were reached (bug 5688).
  // The overview is kept as a fallback: on the client record it is loaded either way.
  const activeTabLink = getActiveTabLink(
    location.pathname,
    openClientContext ??
      (companyOverview
        ? {
            companyRowID: companyOverview.companyRowID,
            clientStatusID: companyOverview.clientStatusID,
          }
        : null),
  );
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [anchorEl, setanchorEl] = useState<null | Element>(null);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState<boolean>(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | Element>(null);

  const handleClick = (event: React.MouseEvent) => {
    setMenuOpen(true);
    setanchorEl(event.currentTarget);
  };

  const handelClose = () => {
    setMenuOpen(false);
  };

  const handleSettingsClick = (event: React.MouseEvent) => {
    setSettingsMenuOpen(true);
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsMenuOpen(false);
  };

  const onLogout = () => {
    authStore.logout();
    return navigate(ROUTE_URLS.LOGIN);
  };

  const onRevert = () => {
    const existingAuthorizationToken = localStorage.getItem("Abp.ImperonatingToken");
    if (existingAuthorizationToken != null) {
      localStorage.removeItem("Abp.ImperonatingToken");
      localStorage.setItem("AuthToken", existingAuthorizationToken);
      const imperonatingUrl = localStorage.getItem("Abp.ImperonatingUrl");
      if (imperonatingUrl != null) {
        localStorage.removeItem("Abp.ImperonatingUrl");
        window.location.href = imperonatingUrl;
      } else {
        window.location.href = "/company";
      }
    }
  };

  return (
    <div>
      <AppBar position="static">
        <Toolbar>
          <Link to={ROUTE_URLS.HOME}>
            <img
              src={tddaLogo}
              alt="TDDA"
              style={{ height: "63px", marginRight: "10px" }}
            />
          </Link>
          {user && Tabs(isGranted).filter(x => x.permission).map((val) => {
            const isActive = val.link === activeTabLink;
            return (
              <Badge
                key={val.name}
                badgeContent={val.showProspectsBadge ? prospectsCount : 0}
                max={999}
                invisible={!val.showProspectsBadge || prospectsCount <= 0}
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                    border: "1px solid #fff",
                  },
                }}
              >
                <Button
                  variant={
                    isActive
                      ? "outlined"
                      : "contained"
                  }
                  disableElevation
                  sx={{
                    "&:hover": {
                      backgroundColor: isActive ? "#fff" : "primary.main",
                    },
                  }}
                  className={
                    isActive
                      ? classes.active
                      : classes.inactive
                  }
                  component={Link}
                  to={val.link}
                  startIcon={val.icons}
                  color={
                    isActive
                      ? "secondary"
                      : "primary"
                  }
                >
                  {val.name}
                </Button>
              </Badge>
            );
          })}

          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1 }}
          ></Typography>
          <Permission permission="CRM.Settings">
            <IconButton
              aria-label="settings"
              onClick={handleSettingsClick}
              color={
                "settings" === location.pathname.split("/")[1]
                  ? "secondary"
                  : "inherit"
              }
            >
              <SettingsIcon />
            </IconButton>
            <Menu
              anchorEl={settingsAnchorEl}
              id="settings-menu"
              open={settingsMenuOpen}
              onClose={handleSettingsClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem
                component={Link}
                to="settings/lead-types"
                onClick={handleSettingsClose}
              >
                <ListItemIcon>
                  <ListIcon fontSize="small" />
                </ListItemIcon>
                Lead Types
              </MenuItem>
              {isGranted("CRM.UserManagement") && (
                <MenuItem
                  component={Link}
                  to="settings/user-management"
                  onClick={handleSettingsClose}
                >
                  <ListItemIcon>
                    <PeopleIcon fontSize="small" />
                  </ListItemIcon>
                  User Management
                </MenuItem>
              )}
            </Menu>
          </Permission>
          <Button
            variant="contained"
            startIcon={<AccountCircleIcon />}
            disableElevation
            sx={{
              "&:hover": {
                backgroundColor: "primary.main",
              },
            }}
            onClick={handleClick}
          >
            <Typography variant="body1" sx={{ marginLeft: "5px" }}>
              {user?.firstName} {user?.lastName}
            </Typography>
          </Button>
          <UserMenu
            open={menuOpen}
            anchorEl={anchorEl}
            onClose={handelClose}
            onLogout={onLogout}
            onRevert={onRevert}
          />
        </Toolbar>

        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) =>
              Math.max.apply(Math, Object.values(theme.zIndex)) + 1,
          }}
          open={uiStore.globalLoading}
        ></Backdrop>
        {uiStore.globalLoading && <LinearProgress />}
      </AppBar>
    </div>
  );
};
export default observer(Header);
